/** Norsk tallformatering. Avrunding skjer kun ved visning. */

const cache = new Map<string, Intl.NumberFormat>();

function nf(min: number, max: number): Intl.NumberFormat {
  const key = `${min}:${max}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: min, maximumFractionDigits: max });
    cache.set(key, f);
  }
  return f;
}

export function fmtNumber(v: number | null | undefined, decimals = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  return nf(decimals, decimals).format(v);
}

export function fmtNumberFlex(v: number | null | undefined, maxDecimals = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  return nf(0, maxDecimals).format(v);
}

export function fmtPct(v: number | null | undefined, decimals = 1, withSign = false): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const s = nf(decimals, decimals).format(Math.abs(v));
  const sign = v < 0 ? '−' : withSign && v > 0 ? '+' : '';
  return `${sign}${s} %`;
}

export function fmtPp(v: number | null | undefined, decimals = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const s = nf(decimals, decimals).format(Math.abs(v));
  const sign = v < 0 ? '−' : v > 0 ? '+' : '';
  return `${sign}${s} pp`;
}

export function fmtSigned(v: number | null | undefined, decimals = 0, unit = ''): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const s = nf(decimals, decimals).format(Math.abs(v));
  const sign = v < 0 ? '−' : v > 0 ? '+' : '';
  return `${sign}${s}${unit ? ` ${unit}` : ''}`;
}

/**
 * Én felles regel for endringstekst: andeler (isShare) vises ALLTID i prosentpoeng (pp),
 * øvrige måltall som relativ prosent. Dette hindrer at «+13,3 pp» også dukker opp som «+13,3 %».
 */
export function fmtChange(row: { isShare?: boolean; ppChange?: number | null; pctChange?: number | null; status?: string } | null | undefined, decimals = 1): string {
  if (!row) return '–';
  if (row.status === 'unavailable') return 'sammenligning utilgjengelig';
  if (row.status === 'cannot') return 'kan ikke beregnes';
  if (row.isShare) return fmtPp(row.ppChange, decimals);
  return fmtPct(row.pctChange, decimals, true);
}

/** Absolutt endring med riktig enhet: andeler får «pp», ikke «%». */
export function fmtAbsChange(row: { isShare?: boolean; absChange?: number | null; unit?: string; decimals?: number } | null | undefined): string {
  if (!row || row.absChange === null || row.absChange === undefined) return '–';
  if (row.isShare) return fmtPp(row.absChange, row.decimals ?? 1);
  return fmtSigned(row.absChange, row.decimals ?? 0, row.unit ?? '');
}

/**
 * Presentasjonsavrunding: fjerner flyttalsstøy (2549.999999 → 2550) uten å røre beregningene.
 * Brukes før en verdi skrives til CSV eller vises uten eksplisitt antall desimaler.
 * Standard: maks 4 desimaler, etterfølgende nuller fjernes.
 */
export const CSV_DEFAULT_MAX_DECIMALS = 4;

export function cleanFloat(v: number, maxDecimals = CSV_DEFAULT_MAX_DECIMALS): number {
  if (!Number.isFinite(v)) return v;
  return Number(v.toFixed(maxDecimals));
}

/**
 * Norsk desimalformat for CSV: komma som desimaltegn, ingen tusenskille.
 * Med `decimals` brukes fast antall desimaler (kolonnens dokumenterte presisjon).
 * Uten `decimals` avrundes til maks 4 desimaler og etterfølgende nuller fjernes – aldri rå flyttall.
 */
export function csvNumber(v: number | null | undefined, decimals?: number): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  const s = decimals === undefined ? String(cleanFloat(v)) : v.toFixed(decimals);
  return s.replace('.', ',');
}

export function sum(values: (number | null | undefined)[]): number {
  let s = 0;
  for (const v of values) if (typeof v === 'number' && Number.isFinite(v)) s += v;
  return s;
}

export function round(v: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}
